import React from 'react';
import { Button as MuiButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import spacing from '../spacing';
import shadows from '../shadows';
import typography from '../typography';

/**
 * Button Component
 * @component
 * @description Modern button with consistent styling and multiple variants
 * 
 * @example
 * // Primary button
 * <Button variant="contained" color="primary">
 *   Guardar
 * </Button>
 * 
 * @example
 * // Outlined button with icon
 * <Button variant="outlined" startIcon={<Add />} onClick={handleAdd}>
 *   Agregar
 * </Button>
 * 
 * @param {string} variant - 'contained' | 'outlined' | 'text'
 * @param {string} color - 'primary' | 'secondary' | 'success' | 'error'
 * @param {string} size - 'small' | 'medium' | 'large'
 */

const StyledButton = styled(MuiButton)(({ variant, size, color, theme }) => ({
  // Base styles
  borderRadius: spacing.borderRadius.md,
  textTransform: 'none',
  fontWeight: typography.fontWeight.semibold,
  transition: 'all 0.2s ease-in-out',
  
  // Size variants
  ...(size === 'small' && {
    padding: `${spacing[1.5]}px ${spacing[3]}px`,
    fontSize: typography.fontSize.sm,
  }),
  ...(size === 'medium' && {
    padding: `${spacing[2]}px ${spacing[4]}px`,
    fontSize: typography.fontSize.base,
  }),
  ...(size === 'large' && {
    padding: `${spacing[3]}px ${spacing[6]}px`,
    fontSize: typography.fontSize.lg,
  }),

  // Variant: contained
  ...(variant === 'contained' && {
    boxShadow: shadows.button,
    '&:hover': {
      boxShadow: shadows.buttonHover,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  }),

  // Variant: outlined
  ...(variant === 'outlined' && {
    borderWidth: spacing.borderWidth[2],
    '&:hover': {
      borderWidth: spacing.borderWidth[2],
    },
  }),

  // Color variants para outlined y text
  ...(variant === 'outlined' && color === 'primary' && {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      borderColor: theme.palette.primary.dark,
      backgroundColor: theme.palette.action.hover,
    },
  }),

  ...(variant === 'text' && color === 'primary' && {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  }),

  // Disabled state
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const Button = React.forwardRef(({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  startIcon,
  endIcon,
  onClick,
  type = 'button',
  ...props
}, ref) => {
  return (
    <StyledButton
      ref={ref}
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onClick}
      type={type}
      {...props}
    >
      {children}
    </StyledButton>
  );
});

Button.displayName = 'Button';

export default Button;
